import React from 'react';

import { getPacklaneBrief, getPacklaneBriefVariables } from '__generated__/getPacklaneBrief';
import { PacklaneBriefFields } from '__generated__/PacklaneBriefFields';
import { AuthConsumer } from 'projects/components/AuthContext';
import FilterDescription from 'projects/components/FilterDescription';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesigners from 'projects/routes/InviteDesignersPage';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { useApolloClient } from 'react-apollo';
import { Route, Switch, useRouteMatch } from 'react-router';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './Packlane.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

interface Props {
    briefId: string;
    step: string;
}

const steps: StepConfiguration[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
    },
    { step: 2, title: __('Your time & budget') },
    { step: 3, title: __('About you') },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        requiresAuth: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export function Packlane() {
    const route = useRouteMatch<Props>();
    const client = useApolloClient();

    return (
        <BriefQuery<getPacklaneBrief, getPacklaneBriefVariables>
            query={queries.getPacklaneBrief}
            variables={{ id: route!.params.briefId }}
            fieldsTypename="PacklaneBriefFields"
        >
            {(data, refetch) => {
                const { viewer } = data;
                const brief = { ...data.brief, fields: data.brief.fields as PacklaneBriefFields };

                const step = createStepState({
                    steps,
                    brief: data.brief,
                    currentStep: parseInt(route!.params.step, 10),
                    designerSources: data.brief.designersWithSource.map(d => d.source),
                });

                return (
                    <AuthConsumer>
                        {({ login }) => (
                            <Brief
                                refetch={refetch}
                                schema="packlane"
                                brief={brief}
                                viewer={viewer}
                                login={login}
                                step={step}
                                client={client}
                            >
                                {form => (
                                    <Switch>
                                        <Route
                                            path={`/briefs/packlane/:briefId/1`}
                                            render={() => <DetailsPage form={form} brief={brief} />}
                                        />
                                        <Route
                                            path={`/briefs/packlane/:briefId/2`}
                                            render={() => (
                                                <TimeAndBudgetPage
                                                    form={form}
                                                    brief={brief}
                                                    site={data.site}
                                                />
                                            )}
                                        />
                                        <Route
                                            path={`/briefs/packlane/:briefId/3`}
                                            render={() => (
                                                <AboutYouPage
                                                    form={form}
                                                    brief={brief}
                                                    industries={data.industries}
                                                />
                                            )}
                                        />
                                        <Route
                                            path={`/briefs/packlane/:briefId/4`}
                                            render={() => (
                                                <SummaryPage
                                                    brief={brief}
                                                    industries={data.industries}
                                                />
                                            )}
                                        />
                                        <Route
                                            path={`/briefs/packlane/:briefId/5`}
                                            render={() => (
                                                <InviteDesigners brief={brief} viewer={data.viewer}>
                                                    <FilterDescription
                                                        industries={data.industries}
                                                        brief={brief}
                                                        category={
                                                            brief.category! && brief.category!.title
                                                        }
                                                        budget={brief.fields.budget}
                                                    />
                                                </InviteDesigners>
                                            )}
                                        />
                                    </Switch>
                                )}
                            </Brief>
                        )}
                    </AuthConsumer>
                );
            }}
        </BriefQuery>
    );
}
